/**
 * First-connect bridge — persistence flags and the (pure) guard predicate.
 *
 * The bridge auto-opens Table Explorer (or a prefilled query tab) after a
 * brand-new install's first user-initiated connect. Two localStorage flags,
 * following the Workstream-A `sample_db_first_query` one-shot pattern, decide
 * who ever sees it:
 *
 *   - `HAS_RUN_QUERY_KEY`   — set the first time ANY query executes (query
 *     editor OR Table Explorer load). Excludes day-2 users and sample-DB users.
 *   - `BRIDGE_FIRED_KEY`    — set the moment the bridge uses its one shot, so
 *     it never fires again on this install.
 *
 * The initiator + environment checks (browser mode, e2e opt-in, sample DB) are
 * threaded in as booleans so `evaluateBridgeGuard` stays pure and unit-testable.
 */

/** localStorage key: "any query has ever run on this install". */
const HAS_RUN_QUERY_KEY = 'jam-sql-has-run-query';

/** localStorage key: "the first-connect bridge has used its one shot". */
const BRIDGE_FIRED_KEY = 'jam-sql-first-connect-bridge-fired';

/**
 * E2E opt-in. In test mode the bridge is OFF by default so it never fires
 * inside the many existing specs that connect; a spec must set this key
 * explicitly to exercise it (mirrors the B2/B3 `sql-studio-e2e-*` seams).
 */
export const BRIDGE_TEST_OPT_IN_KEY = 'sql-studio-e2e-first-connect-bridge';

/** Who asked for a connect — only genuine user connects are bridge-eligible. */
export type ConnectInitiator = 'user' | 'restore';

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // Storage unavailable — worst case the bridge could re-fire once; harmless.
  }
}

/** True once any query (editor or Table Explorer) has executed on this install. */
export function hasRunQuery(): boolean {
  return readFlag(HAS_RUN_QUERY_KEY);
}

/** Record that a query has executed. Idempotent, fire-and-forget. */
export function markHasRunQuery(): void {
  writeFlag(HAS_RUN_QUERY_KEY);
}

/** True once the bridge has used its single, permanent shot. */
export function hasBridgeFired(): boolean {
  return readFlag(BRIDGE_FIRED_KEY);
}

/** Consume the bridge's one shot — permanent, never reset by the app. */
export function markBridgeFired(): void {
  writeFlag(BRIDGE_FIRED_KEY);
}

/**
 * Restore / auto-reconnect connects are never a "first connect" moment. This
 * is the seam-level gate applied at `connectToDatabase`; anything not tagged
 * `'restore'` is treated as user-initiated.
 */
export function shouldConsiderConnectForBridge(initiator: ConnectInitiator | undefined): boolean {
  return initiator !== 'restore';
}

/** What the guard decided the bridge should do for this connect. */
export type BridgeGuardDecision =
  /** Open the landing surface (Table Explorer, else prefilled query tab). */
  | 'run'
  /** The user already opened a tab — burn the one shot but open nothing. */
  | 'consume-only'
  /** Not eligible; leave the one shot intact. */
  | 'skip';

export type BridgeGuardInput = {
  /** Desktop only — the bridge is skipped entirely in browser mode. */
  readonly isBrowserMode: boolean;
  /** True when running under the E2E harness. */
  readonly isTestMode: boolean;
  /** Whether the E2E opt-in key is present (only consulted in test mode). */
  readonly hasTestOptIn: boolean;
  /** The bundled sample DB lands in its own query tab — never bridge it. */
  readonly isSampleConnection: boolean;
  /** Any query has already executed on this install. */
  readonly hasRunQuery: boolean;
  /** The bridge already used its one shot. */
  readonly hasBridgeFired: boolean;
  /** The user already opened a real workspace tab (anything but the start page). */
  readonly hasOpenWorkspaceTab: boolean;
};

/**
 * Pure guard. Order matters: the hard excludes (that must NOT consume the one
 * shot) come first; only after those pass do we commit to burning the shot —
 * either opening the surface (`run`) or, if the user beat us to it, skipping
 * the surface while still consuming the shot (`consume-only`, so "one shot
 * ever" holds even when they opened a tab themselves).
 */
export function evaluateBridgeGuard(input: BridgeGuardInput): BridgeGuardDecision {
  if (input.isBrowserMode) return 'skip';
  if (input.isTestMode && !input.hasTestOptIn) return 'skip';
  if (input.isSampleConnection) return 'skip';
  if (input.hasRunQuery) return 'skip';
  if (input.hasBridgeFired) return 'skip';

  // Committed to using the one shot from here on.
  if (input.hasOpenWorkspaceTab) return 'consume-only';
  return 'run';
}
